import React, { useState, useContext } from 'react'

import { Link } from 'react-router-dom'
import useStructureParams from '../lib/useStructureParams'
import { filterAndSortChildren, wardNamesCleanup } from '../lib/structures'
import { useMicroTargeting } from '../data/useMicroTargeting'

import PageTitle from './PageTitle'
import Breadcrumbs from './Breadcrumbs'
import PageHeader from './PageHeader'
import ViewButtons from './ViewButtons'
import ResponsiblePersons from './ResponsiblePersons'

import Table from 'react-bootstrap/Table'
import { Col, Row } from 'react-bootstrap'

import { LoadingSpinner } from './spinners'
import { NoAssignedWardsMessage } from './alerts'
import { statsStyle, formatInt, formatPerc } from '../lib/utils'

import {
  Chart as ChartJS,
  registerables
} from 'chart.js'
import 'chartjs-adapter-date-fns'
import annotationPlugin from 'chartjs-plugin-annotation'

import { Line } from 'react-chartjs-2'

import Map from './Map'
import { Phase, PhaseColor } from '../lib/microTargeting'
import VDCategoryFilter from './VDCategoryFilter'
import { DashboardContext } from '../data/context'
import HeaderLayout from "./HeaderLayout"

ChartJS.register(annotationPlugin, ...registerables)

export default function MicroTargetingView(): JSX.Element {
  const { filterLabel } = useContext(DashboardContext)
  const [mapChoice, setMapChoice] = useState<string>('phase1')
  // get parameters from the URL
  const { code, type } = useStructureParams()

  // get stats from the GraphQL API
  const { stats, loading } = useMicroTargeting({ type, code, filterLabel })
  // this happens while the query is still fetching
  if (stats == null || loading) {
    return <LoadingSpinner />
  }

  // filter/sort children appropriately according to structure type
  const children = filterAndSortChildren(stats.type, stats.children)
    .map(wardNamesCleanup)
    .filter(c => c.structureHasAssignedWards)
    .sort((a, b) => {
      const getProgress = (compare: typeof a) => compare.microtargeting.phase1.target ? compare.microtargeting.phase1.current / compare.microtargeting.phase1.target : 0
      const progressDiff = getProgress(b) - getProgress(a)

      if (progressDiff !== 0) return progressDiff
      return (a.name ?? '').localeCompare(b.name ?? '')
    })

  const mapColor = (code: string): string => {
    const c = children.find(c => c.code === code)
    if (c == null) return '#ffffff'
    // @ts-expect-error
    const color = statsStyle(c.microtargeting[mapChoice]?.current ?? 0, c.microtargeting[mapChoice]?.target ?? 0).backgroundColor
    return color
  }

  const historicDateSets = () => {
    const historicRollLine = []
    for (const phase of Object.values(stats.microtargeting)) {
      if (phase?.historic) {
        historicRollLine.push(
          {
            label: Phase[phase.__typename as keyof typeof Phase] ?? phase.__typename,
            // @ts-expect-error
            data: phase.historic.map(h => ({ x: h.date, y: h.count })),
            borderColor: PhaseColor[phase.__typename as keyof typeof Phase] ?? 'grey'
          }
        )
      }
    }
    return historicRollLine
  }

  const graphData = {
    datasets: historicDateSets()
  }

  const graphOptions = {
    interaction: {
      intersect: false,
      mode: 'index'
    },
    animation: false,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'day',
          tooltipFormat: 'd LLL yyyy'
        }
      },
      y: {
        beginAtZero: true
      }
    },
    plugins: {
      autocolors: false,
      annotation: {
        annotations: null
      }
    }
  }

  if (!stats.structureHasAssignedWards) {
    return (
      <>
        <PageTitle title={stats.name} />
        <Breadcrumbs structure={stats} suffix='/microtargeting' />
        {/* <PageHeader structure={stats} /> */}
        <HeaderLayout
           element1={<PageHeader structure={stats} />}
           element2={<ViewButtons structure={stats} section="microtargeting" />}
           element3={<VDCategoryFilter counts={stats.counts} />}
        />
        {/* <ViewButtons structure={stats} section='microtargeting' /> */}

        <NoAssignedWardsMessage type={stats.type} />
      </>
    )
  } else {
    return (
      <>
        <PageTitle title={stats.name} />
        <Breadcrumbs structure={stats} suffix='/microtargeting' />
        {/* <PageHeader structure={stats} /> */}
        <HeaderLayout
           element1={<PageHeader structure={stats} />}
           element2={<ViewButtons structure={stats} section="microtargeting" />}
           element3={<VDCategoryFilter counts={stats.counts} />}
        />
        {/* <ViewButtons structure={stats} section='microtargeting' /> */}

        <Row>
          <Col md={6}>
            <div style={{ position: 'relative', height: '400px', width: '100%' }}>
              {/* @ts-expect-error */}
              <Line options={graphOptions} data={graphData} />

            </div>
          </Col>
          <Col className='text-center' md={6}>
            <Map type={type} code={code} childStructures={stats.children} childColourFn={mapColor} />
            <br />
            <b>Show on map:&nbsp;</b>
            <select value={mapChoice} onChange={event => setMapChoice(event.target.value)}>
              <option value='phase1'>First Survey - Listening Phase</option>
              <option value='phase2'>Second Survey - Solution Phase</option>
              <option value='phase3'>Third Survey - Confirmation Phase</option>
            </select>

          </Col>
        </Row>
        <br />
        <Table responsive striped className='break-before stats'>
          <thead>
            <th colSpan={2}> </th>
            <th style={{ borderLeft: '1px solid #ccc' }} className='text-center' colSpan={3}>First Survey - Listening Phase <br /> (01 Nov 2025 - 17 May 2026)</th>
            <th style={{ borderLeft: '1px solid #ccc' }} className='text-center' colSpan={3}>Second Survey - Solution Phase <br /> (18 May 2026 - 13 Sep 2026)</th>
            <th style={{ borderLeft: '1px solid #ccc' }} className='text-center' colSpan={3}>Third Survey - Confirmation Phase <br /> (14 Sep 2026 - 11 Oct 2026)</th>
            <tr>
              <th />
              <th className='text-center'>VD Counts</th>
              <th style={{ borderLeft: '1px solid #ccc' }} className='text-center'>Unique Surveys Achieved</th>
              <th className='text-center'>Survey Target</th>
              <th className='text-center'>Unique Surveys Achieved as a % of Target</th>
              <th style={{ borderLeft: '1px solid #ccc' }} className='text-center'>Unique Surveys Achieved</th>
              <th className='text-center'>Survey Target</th>
              <th className='text-center'>Unique Surveys Achieved as a % of Target</th>
              <th style={{ borderLeft: '1px solid #ccc' }} className='text-center'>Unique Surveys Achieved</th>
              <th className='text-center'>Survey Target</th>
              <th className='text-center'>Unique Surveys Achieved as a % of Target</th>
            </tr>
          </thead>
          <tbody>
            {children.map(c => (
              <tr key={c.code}>
                {stats.type !== 'ward' ? <td><Link to={`/${c.type}/${c.code}/microtargeting`}>{c.name}</Link> <ResponsiblePersons persons={c.responsiblePersons} /></td> : <td>{c.name}</td>}
                <td className='text-center'>{`${formatInt(c.microtargeting?.vdcounts?.included)} of ${formatInt(c.counts.total)}`}</td>
                <td style={{ borderLeft: '1px solid #ccc' }} className='text-center'>{formatInt(c.microtargeting.phase1.current)}</td>
                <td className='text-center'>{formatInt(c.microtargeting.phase1.target)}</td>
                <td style={statsStyle(c.microtargeting.phase1.current, c.microtargeting.phase1.target)} className='text-center'>{formatPerc(c.microtargeting.phase1.current / c.microtargeting.phase1.target)}</td>
                <td style={{ borderLeft: '1px solid #ccc' }} className='text-center'>{formatInt(c.microtargeting.phase2.current)}</td>
                <td className='text-center'>{formatInt(c.microtargeting.phase2.target)}</td>
                <td style={statsStyle(c.microtargeting.phase2.current, c.microtargeting.phase2.target)} className='text-center'>{formatPerc(c.microtargeting.phase2.current / c.microtargeting.phase2.target)}</td>
                <td style={{ borderLeft: '1px solid #ccc' }} className='text-center'>{formatInt(c.microtargeting.phase3.current)}</td>
                <td className='text-center'>{formatInt(c.microtargeting.phase3.target)}</td>
                <td style={statsStyle(c.microtargeting.phase3.current, c.microtargeting.phase3.target)} className='text-center'>{formatPerc(c.microtargeting.phase3.current / c.microtargeting.phase3.target)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className='font-weight-bold'>
              <td>Total</td>
              <td className='text-center'>{`${formatInt(stats.microtargeting?.vdcounts?.included)} of ${formatInt(stats.counts.total)}`}</td>
              <td style={{ borderLeft: '1px solid #ccc' }} className='text-center'>{formatInt(stats.microtargeting.phase1.current)}</td>
              <td className='text-center'>{formatInt(stats.microtargeting.phase1.target)}</td>
              <td style={statsStyle(stats.microtargeting.phase1.current, stats.microtargeting.phase1.target)} className='text-center'>{formatPerc(stats.microtargeting.phase1.current / stats.microtargeting.phase1.target)}</td>
              <td style={{ borderLeft: '1px solid #ccc' }} className='text-center'>{formatInt(stats.microtargeting.phase2.current)}</td>
              <td className='text-center'>{formatInt(stats.microtargeting.phase2.target)}</td>
              <td style={statsStyle(stats.microtargeting.phase2.current, stats.microtargeting.phase2.target)} className='text-center'>{formatPerc(stats.microtargeting.phase2.current / stats.microtargeting.phase2.target)}</td>
              <td style={{ borderLeft: '1px solid #ccc' }} className='text-center'>{formatInt(stats.microtargeting.phase3.current)}</td>
              <td className='text-center'>{formatInt(stats.microtargeting.phase3.target)}</td>
              <td style={statsStyle(stats.microtargeting.phase3.current, stats.microtargeting.phase3.target)} className='text-center'>{formatPerc(stats.microtargeting.phase3.current / stats.microtargeting.phase3.target)}</td>
            </tr>
          </tfoot>
        </Table>
      </>
    )
  }
}
